<template>
  <div>
    <v-dialog
      :value="dialog_stage === 'screen_changed'"
      :attach="fullscreen_element || false"
      persistent
      max-width="600"
    >
      <v-card data-testid="screen_calibration_monitor_screen_changed_dialog">
        <v-card-title>{{ $t("calibration.screen_changed_title") }}</v-card-title>
        <v-card-text>
          {{ $t("calibration.screen_changed_message") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            data-testid="screen_calibration_monitor_same_screen"
            @click="accept_same_screen"
          >
            {{ $t("calibration.same_screen") }}
          </v-btn>
          <v-btn
            color="primary"
            data-testid="screen_calibration_monitor_different_screen"
            @click="choose_different_screen"
          >
            {{ $t("calibration.different_screen") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :value="dialog_stage === 'recalibrate'"
      :attach="fullscreen_element || false"
      persistent
      max-width="600"
    >
      <v-card data-testid="screen_calibration_monitor_recalibrate_dialog">
        <v-card-title>{{ $t("calibration.recalibrate_new_screen_title") }}</v-card-title>
        <v-card-text>
          {{ $t("calibration.recalibrate_new_screen_message") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            data-testid="screen_calibration_monitor_cancel"
            @click="cancel_recalibration"
          >
            {{ $t("calibration.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            data-testid="screen_calibration_monitor_calibrate"
            @click="open_calibration"
          >
            {{ $t("calibration.calibrate") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      :value="orientation_offer_visible"
      :attach="fullscreen_element || false"
      persistent
      max-width="600"
    >
      <v-card data-testid="screen_calibration_monitor_orientation_dialog">
        <v-card-title>{{ $t("calibration.sync_orientation_title") }}</v-card-title>
        <v-card-text>{{ $t("calibration.sync_orientation_message", { orientation: proposed_orientation_label }) }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text data-testid="screen_calibration_monitor_keep_orientation" @click="decline_orientation">
            {{ $t("calibration.keep_orientation") }}
          </v-btn>
          <v-btn color="primary" data-testid="screen_calibration_monitor_sync_orientation" @click="accept_orientation">
            {{ $t("calibration.sync_orientation") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { Calibration } from "../classes/calibration";
import {
  calibrationStillMatchesDevice,
  calibrationMatchesCurrentDevice,
  currentCalibrationDeviceIdentity,
} from "../lib/calibration_core";

import { calibrationSync, compareCalibrationSaves } from "../lib/calibration_sync";
import { effectiveAnaglyphOrientation } from "../lib/anaglyph_orientation";

const SCREEN_CHECK_INTERVAL_MS = 500;

export default {
  name: "ScreenCalibrationMonitor",
  data() {
    return {
      dialog_stage: null,
      pending_orientation: null,
      latest_orientation_event: null,
      declined_orientation: null,
      observed_orientation: null,
      queued_messages: [],
      observed_identity: null,
      visible: document.visibilityState === "visible",
      fullscreen_element: null,
      unsubscribe_calibration: null,
      previous_calibration: null,
      ignored_device_fingerprint: null,
      resolved_device_fingerprint: null,
      screen_check_interval: null,
      saving_same_screen: false,
    };
  },
  computed: {
    ...mapState([
      "current_user",
      "current_calibration",
      "user_metadata_loading",
      "current_clinic_prefs",
      "app_config",
      "calibration_dialog",
      "calibration_draft_orientation",
      "active_exercise_ids",
    ]),
    orientation_offer_visible() {
      return Boolean(this.pending_orientation && this.visible &&
        !this.active_exercise_ids.length && !this.user_metadata_loading &&
        this.can_offer_orientation(this.pending_orientation));
    },
    proposed_orientation_label() {
      const keys = {
        "red-cyan": "red_left_blue_right", "cyan-red": "blue_left_red_right",
        "red-green": "red_left_green_right", "green-red": "green_left_red_right",
      };
      const orientation = this.pending_orientation?.calibration.anaglyph_orientation;
      return orientation ? this.$t(`calibration.${keys[orientation]}`) : "";
    },
  },
  watch: {
    current_calibration() {
      if (calibrationMatchesCurrentDevice(this.current_calibration)) {
        this.dialog_stage = null;
        this.previous_calibration = null;
        this.ignored_device_fingerprint = null;
      }
      this.check_screen();
    },
    "current_user.id"() {
      this.reset_sync_context();
      this.check_screen();
    },
    user_metadata_loading() {
      if (!this.user_metadata_loading) {
        this.resolved_device_fingerprint = null;
        const messages = this.queued_messages;
        this.queued_messages = [];
        messages.sort(compareCalibrationSaves).forEach(this.receive_calibration);
      }
      this.check_screen();
    },
  },
  mounted() {
    this.unsubscribe_calibration = calibrationSync(this.app_config.id).subscribe(this.receive_calibration);
    window.addEventListener("resize", this.check_screen);
    window.addEventListener("focus", this.check_screen);
    document.addEventListener("visibilitychange", this.handle_visibility_change);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.check_screen);
    }
    this.screen_check_interval = window.setInterval(
      this.check_screen,
      SCREEN_CHECK_INTERVAL_MS,
    );
    this.check_screen();
  },
  beforeDestroy() {
    this.unsubscribe_calibration?.();
    window.removeEventListener("resize", this.check_screen);
    window.removeEventListener("focus", this.check_screen);
    document.removeEventListener("visibilitychange", this.handle_visibility_change);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.check_screen);
    }
    if (this.screen_check_interval !== null) {
      window.clearInterval(this.screen_check_interval);
    }
  },
  methods: {
    reset_sync_context() {
      this.pending_orientation = null;
      this.latest_orientation_event = null;
      this.declined_orientation = null;
      this.observed_orientation = null;
      this.queued_messages = [];
      this.dialog_stage = null;
      this.previous_calibration = null;
      this.ignored_device_fingerprint = null;
      this.resolved_device_fingerprint = null;
    },
    receive_calibration(message) {
      if (!this.current_user || message.user_id !== this.current_user.id) return;
      if (this.user_metadata_loading) {
        this.queued_messages.push(message);
        return;
      }
      const sync = calibrationSync(this.app_config.id);
      if (!sync.accept(message)) return;
      this.check_screen();
      const matching = calibrationMatchesCurrentDevice(message.calibration);
      if (matching) {
        this.$store.commit("applySavedCalibration", Calibration.fromJson(message.calibration));
        this.$store.commit("markCalibrationSynchronized");
        this.dialog_stage = null;
        this.previous_calibration = null;
        this.ignored_device_fingerprint = null;
      }
      if (this.latest_orientation_event && compareCalibrationSaves(message, this.latest_orientation_event) <= 0) return;
      this.latest_orientation_event = message;
      const orientation = message.calibration.anaglyph_orientation;
      if (orientation !== this.observed_orientation) this.declined_orientation = null;
      this.observed_orientation = orientation;
      this.pending_orientation = !matching && this.can_offer_orientation(message) ? message : null;
    },
    can_offer_orientation(message) {
      if (!this.current_user || message.user_id !== this.current_user.id ||
          !this.observed_identity ||
          !calibrationStillMatchesDevice(this.observed_identity, currentCalibrationDeviceIdentity()) ||
          calibrationMatchesCurrentDevice(message.calibration) ||
          this.current_clinic_prefs?.disallow_different_glasses ||
          !calibrationSync(this.app_config.id).isLatestForUser(message)) return false;
      const calibrated = calibrationMatchesCurrentDevice(this.current_calibration);
      if (!calibrated && !this.calibration_dialog) return false;
      const orientation = message.calibration.anaglyph_orientation;
      const current = this.calibration_dialog ? this.calibration_draft_orientation :
        this.current_calibration?.anaglyph_orientation;
      return orientation !== this.declined_orientation && orientation !== effectiveAnaglyphOrientation(
        current, this.current_clinic_prefs, this.app_config,
      );
    },
    decline_orientation() {
      this.declined_orientation = this.pending_orientation?.calibration.anaglyph_orientation;
      this.pending_orientation = null;
    },
    accept_orientation() {
      const message = this.pending_orientation;
      if (!message || !this.orientation_offer_visible) return;
      const orientation = message.calibration.anaglyph_orientation;
      this.pending_orientation = null;
      if (this.calibration_dialog) {
        this.$store.commit("setCalibrationDraftOrientation", orientation);
      }
      if (calibrationMatchesCurrentDevice(this.current_calibration)) {
        const calibration = Calibration.fromJson(JSON.parse(JSON.stringify(this.current_calibration)));
        calibration.anaglyph_orientation = orientation;
        this.$store.dispatch("updateCurrentCalibration", calibration);
      }
    },
    handle_visibility_change() {
      if (document.visibilityState === "visible") {
        this.check_screen();
      }
    },
    check_screen() {
      this.visible = document.visibilityState === "visible";
      this.fullscreen_element = document.fullscreenElement || document.webkitFullscreenElement || null;
      const current_identity = currentCalibrationDeviceIdentity();
      // What another tab has told us is only stale once this tab is looking at
      // a different screen. Page zoom moves devicePixelRatio and a rotation
      // swaps screen.width with screen.height, both of which change the
      // fingerprint without the screen having changed, so the comparison is the
      // same tolerant one that decides whether a calibration still applies.
      if (
        this.observed_identity &&
        !calibrationStillMatchesDevice(this.observed_identity, current_identity)
      ) {
        this.reset_sync_context();
      }
      this.observed_identity = current_identity;

      // The screen in front of us may be one the user has already calibrated:
      // swapping between a built-in display and an external monitor is routine,
      // and each screen a user calibrates is saved as a record of its own.
      // Those records are matched against the screen only as they load, so a
      // screen arrived at later in the session is resolved here rather than
      // asked about as though it were new. Once per screen is enough -- the
      // answer only changes when the screen or the records do, and both reset
      // this.
      if (
        this.current_user &&
        !this.user_metadata_loading &&
        this.resolved_device_fingerprint !== current_identity.fingerprint &&
        !calibrationMatchesCurrentDevice(this.current_calibration, current_identity)
      ) {
        this.resolved_device_fingerprint = current_identity.fingerprint;
        this.$store.commit("resolveCurrentCalibration");
      }

      if (
        this.dialog_stage ||
        this.saving_same_screen ||
        !this.current_user ||
        this.user_metadata_loading ||
        !this.current_calibration ||
        // An exercise is mid-flight and owns the screen. A v2 exercise already
        // fails loudly on a calibration that no longer matches, so prompting
        // over the top of it would interrupt without adding anything; the next
        // check once it ends picks this up.
        this.active_exercise_ids.length
      ) {
        return;
      }

      if (
        current_identity.fingerprint === this.ignored_device_fingerprint ||
        calibrationStillMatchesDevice(this.current_calibration, current_identity)
      ) {
        return;
      }

      this.previous_calibration = Calibration.fromJson(
        JSON.parse(JSON.stringify(this.current_calibration)),
      );
      this.dialog_stage = "screen_changed";
    },
    async accept_same_screen() {
      if (!this.previous_calibration) {
        return;
      }

      const calibration = Calibration.fromPreviousForCurrentDevice(
        this.previous_calibration,
      );
      this.dialog_stage = null;
      this.previous_calibration = null;
      this.ignored_device_fingerprint = null;
      this.saving_same_screen = true;
      try {
        await this.$store.dispatch("updateCurrentCalibration", calibration);
      } finally {
        this.saving_same_screen = false;
        this.check_screen();
      }
    },
    choose_different_screen() {
      this.ignored_device_fingerprint =
        currentCalibrationDeviceIdentity().fingerprint;
      this.$store.commit("clearCurrentCalibration");
      this.dialog_stage = "recalibrate";
    },
    open_calibration() {
      this.dialog_stage = null;
      this.previous_calibration = null;
      this.$store.commit("setCalibrationDialog", true);
    },
    cancel_recalibration() {
      this.dialog_stage = null;
      this.previous_calibration = null;
    },
  },
};
</script>
